import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { emailService } from '@/services/emailService';
import type { EmailSendingConfig, EmailDomainStatus } from '@/types/email';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';

function statusVariant(status?: EmailDomainStatus): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'VERIFICADO') return 'default';
  if (status === 'FALHOU') return 'destructive';
  return 'outline';
}

export default function EmailDominiosTab() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';

  const [dominio, setDominio] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [replyTo, setReplyTo] = useState('');

  const { data: config, isLoading, isError } = useQuery<EmailSendingConfig | null>({
    queryKey: ['email-dominio'],
    queryFn: () => emailService.getDominio(),
  });

  const salvar = useMutation({
    mutationFn: () =>
      emailService.setDominio({
        dominio,
        from_email: fromEmail || undefined,
        from_name: fromName || undefined,
        reply_to: replyTo || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-dominio'] });
      toast({ title: 'Domínio configurado', description: 'Adicione os registros DKIM no DNS para verificar.' });
      setDominio(''); setFromEmail(''); setFromName(''); setReplyTo('');
    },
    onError: () => toast({ title: 'Erro', description: 'Falha ao configurar domínio.', variant: 'destructive' }),
  });

  const verificar = useMutation({
    mutationFn: () => emailService.verificarDominio(),
    onSuccess: (d) => {
      queryClient.invalidateQueries({ queryKey: ['email-dominio'] });
      toast({ title: 'Status atualizado', description: `Status atual: ${d.status}` });
    },
    onError: () => toast({ title: 'Erro', description: 'Falha ao consultar status.', variant: 'destructive' }),
  });

  const remover = useMutation({
    mutationFn: () => emailService.removerDominio(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-dominio'] });
      toast({ title: 'Domínio removido' });
    },
    onError: () => toast({ title: 'Erro', description: 'Falha ao remover domínio.', variant: 'destructive' }),
  });

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Carregando...</p>;
  }

  if (isError) {
    return <p className="text-sm text-destructive">Erro ao carregar o domínio de envio.</p>;
  }

  // Não-admin: somente leitura do status do domínio de envio.
  if (!isAdmin) {
    return (
      <Card>
        <CardHeader><CardTitle>Domínio de envio</CardTitle></CardHeader>
        <CardContent>
          {config ? (
            <div className="flex items-center gap-3">
              <p className="font-medium">{config.dominio}</p>
              <Badge variant={statusVariant(config.status)}>{config.status}</Badge>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Nenhum domínio de envio configurado.</p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Admin: gerência completa.
  return (
    <div className="space-y-6">
      {!config && (
        <Card>
          <CardHeader><CardTitle>Configurar domínio de envio</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="dominio">Domínio</Label>
                <Input id="dominio" placeholder="mail.atomos.tech" value={dominio} onChange={(e) => setDominio(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="from_email">Email remetente padrão (From)</Label>
                <Input id="from_email" placeholder="cobranca@mail.atomos.tech" value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="from_name">Nome do remetente padrão</Label>
                <Input id="from_name" placeholder="Átomos" value={fromName} onChange={(e) => setFromName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="reply_to">Reply-To padrão</Label>
                <Input id="reply_to" placeholder="financeiro@atomos.tech" value={replyTo} onChange={(e) => setReplyTo(e.target.value)} />
              </div>
            </div>
            <Button onClick={() => salvar.mutate()} disabled={!dominio || salvar.isPending}>
              {salvar.isPending ? 'Configurando...' : 'Configurar e gerar DKIM'}
            </Button>
          </CardContent>
        </Card>
      )}

      {config && (
        <Card>
          <CardHeader><CardTitle>Domínio de envio</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="font-medium">{config.dominio}</p>
                {(config.from_name || config.from_email) && (
                  <p className="text-sm text-muted-foreground">
                    {config.from_name} {config.from_email ? `<${config.from_email}>` : ''}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={statusVariant(config.status)}>{config.status}</Badge>
                <Button size="sm" variant="outline" onClick={() => verificar.mutate()} disabled={verificar.isPending || remover.isPending}>
                  Verificar agora
                </Button>
                <Button size="sm" variant="destructive" onClick={() => remover.mutate()} disabled={verificar.isPending || remover.isPending}>
                  Remover
                </Button>
              </div>
            </div>

            {config.dkim_tokens?.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Registros DKIM (adicione no DNS do domínio):</p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {config.dkim_tokens.map((rec) => (
                      <TableRow key={rec.name}>
                        <TableCell>{rec.type}</TableCell>
                        <TableCell className="font-mono text-xs break-all">{rec.name}</TableCell>
                        <TableCell className="font-mono text-xs break-all">{rec.value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
